import { BUNDLED_REFERENCE } from "./referenceData";

// This table replaces it with the real distribution: measured raw value at each of the 1st.
// 99th nationwide percentiles, so the scale now answers "what share of ground reads below this
// cell" rather than "where does this sit between SOURCE: `Behavior Zone Classification Thresholds -
// Sheet2.

export type BehaviorKey = "moisture" | "productivity" | "fertility";

let served: Record<BehaviorKey, readonly number[]> | null = null;

/* * Adopt compute's table. Called once at app start; safe to call again if the response is refetched. */
export function setServedPercentiles(
  t: Record<BehaviorKey, readonly number[]> | null,
): void {
  // a malformed or short payload is REFUSED rather than half-adopted — a partial table would bend the
  // scale in a way no error would announce
  if (!t) return;
  const ok = (["moisture", "productivity", "fertility"] as BehaviorKey[]).every(
    (k) => {
      const a = t[k];
      return (
        Array.isArray(a) &&
        a.length === 99 &&
        a.every((v) => Number.isFinite(v)) &&
        a.every((v, i) => i === 0 || v > a[i - 1]!)
      );
    },
  );
  served = ok ? t : null;
}

/* * True once compute's table is in use — for tests and for the doc, not for behaviour. */
export const usingServedPercentiles = (): boolean => served !== null;

/* * Raw value at the Nth percentile, N = index + 1 (so [0] is the 1st, [98] the 99th). */
export const PERCENTILE_TABLE = new Proxy(
  {} as Record<BehaviorKey, readonly number[]>,
  {
    get: (_target, key: string) => served?.[key as BehaviorKey],
    ownKeys: () => (served ? Object.keys(served) : []),
    getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
  },
);

/* * Percentile (1-99) that each table row represents. */
const pctAt = (i: number): number => i + 1;

/*
 *
 * OUTSIDE the table the terminal segment's slope is extrapolated and the result CLAMPED to [0, 100]
 * unlike the old linear scale, a percentile genuinely cannot exceed its bounds, and extrapolating
 * first (rather than flattening straight to 0/100) keeps the ordering of cells inside the extreme
 * tails.
 */
export function toPct(
  v: number | null | undefined,
  key: BehaviorKey,
): number | null {
  if (v == null || !Number.isFinite(v)) return null;
  const t = PERCENTILE_TABLE[key];
  if (!t || t.length < 2) return null;
  const n = t.length;
  if (v <= t[0]!) {
    const slope = 1 / (t[1]! - t[0]!); // percentile points per raw unit at the bottom
    return Math.max(0, pctAt(0) + (v - t[0]!) * slope);
  }
  if (v >= t[n - 1]!) {
    const slope = 1 / (t[n - 1]! - t[n - 2]!);
    return Math.min(100, pctAt(n - 1) + (v - t[n - 1]!) * slope);
  }
  // binary search for the bracketing pair
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (t[mid]! <= v) lo = mid;
    else hi = mid;
  }
  const span = t[hi]! - t[lo]!;
  const f = span > 0 ? (v - t[lo]!) / span : 0;
  return pctAt(lo) + f * (pctAt(hi) - pctAt(lo));
}

/* * The inverse: a 0-100 percentile back to raw index units (for raw-scale consumers and tests). */
export function toIndexScale(pct100: number, key: BehaviorKey): number {
  const t = PERCENTILE_TABLE[key];
  if (!t || t.length < 2) return Number.NaN;
  const n = t.length;
  if (!Number.isFinite(pct100)) return t[0]!;
  if (pct100 <= pctAt(0)) {
    const slope = t[1]! - t[0]!; // raw units per percentile point
    return t[0]! + (pct100 - pctAt(0)) * slope;
  }
  if (pct100 >= pctAt(n - 1)) {
    const slope = t[n - 1]! - t[n - 2]!;
    return t[n - 1]! + (pct100 - pctAt(n - 1)) * slope;
  }
  const i = Math.floor(pct100) - 1; // pctAt(i) <= pct100 < pctAt(i+1)
  const f = pct100 - pctAt(i);
  return t[i]! + f * (t[i + 1]! - t[i]!);
}

setServedPercentiles(BUNDLED_REFERENCE["behavior-percentiles"].thresholds);
